import React from "react";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

interface PaginationProps {
  currentPage: number; // Now should be zero-based
  totalPages: number;
  onChangePage: (page: number) => void;
  hasDataOnLastPage: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages = 5,
  onChangePage,
  hasDataOnLastPage,
}) => {
  const handlePageChange = (page: number) => {
    if (
      page !== currentPage &&
      page >= 0 &&
      page < totalPages &&
      (page !== totalPages - 1 || hasDataOnLastPage)
    ) {
      onChangePage(page);
    }
  };

  const renderPageNumbers = () => {
    if (totalPages <= 1) return null;
    const pages: React.ReactNode[] = [];
    const adjustedCurrentPage = Math.min(currentPage, totalPages - 1);

    const showLeftEllipsis = adjustedCurrentPage > 2;
    const showRightEllipsis = adjustedCurrentPage < totalPages - 3;

    const startPage = showLeftEllipsis
      ? Math.max(adjustedCurrentPage - 1, 1)
      : 1;
    const endPage = showRightEllipsis
      ? Math.min(adjustedCurrentPage + 1, totalPages - 2)
      : totalPages - 2;

    // First page
    pages.push(
      <button
        key={0}
        onClick={() => handlePageChange(0)}
        data-testid="page-number"
        className={`mx-0.5 text-sm hover:text-black text-primary font-semibold py-2 px-4 rounded-md ${
          adjustedCurrentPage === 0 ? "border border-sour text-sour" : ""
        }`}
      >
        1
      </button>
    );

    if (showLeftEllipsis) {
      pages.push(
        <span key="left-ellipsis" className="mx-1 text-gray-500">
          ...
        </span>
      );
    }


    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          data-testid="page-number"
          className={`mx-0.5 text-sm hover:text-black text-primary font-semibold py-2 px-4 rounded-md ${
            adjustedCurrentPage === page ? "border border-sour text-sour" : ""
          }`}
        >
          {page + 1}
        </button>
      );
    }

    if (showRightEllipsis) {
      pages.push(
        <span key="right-ellipsis" className="mx-1 text-gray-500">
          ...
        </span>
      );
    }

    // Last page
    if (totalPages > 1) {
      pages.push(
        <button
          key={totalPages - 1}
          onClick={() => handlePageChange(totalPages - 1)}
          data-testid="page-number"
          className={`mx-0.5 text-sm hover:text-black text-primary font-semibold py-2 px-4 rounded-md ${
            adjustedCurrentPage === totalPages - 1
              ? "border border-sour text-sour"
              : ""
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <nav
      className="px-5 py-5 flex flex-col xs:flex-row items-center xs:justify-between"
      aria-label="Pagination"
    >
      <div className="inline-flex mt-2 xs:mt-0">
        <button
          onClick={() => handlePageChange(0)}
          disabled={currentPage === 0}
          data-testid="page-first"
          className="ml-0.5 text-sm hover:text-white text-primary font-semibold py-2 px-4 rounded-l-md"
          aria-label="First page"
        >
          <SkipPreviousIcon sx={{ color: "#641752" }} />
        </button>

        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 0}
          data-testid="page-prev"
          className="ml-0.5 text-sm hover:text-white text-primary font-semibold py-2 px-4 rounded-l-md"
          aria-label="Previous page"
        >
          <PlayArrowIcon sx={{ transform: "rotate(180deg)", color: "#641752" }} />
        </button>

        {renderPageNumbers()}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
          data-testid="page-next"
          className="ml-0.5 text-sm hover:text-white text-primary font-semibold py-2 px-4 rounded-r-md"
          aria-label="Next page"
        >
          <PlayArrowIcon sx={{ color: "#641752" }} />
        </button>

        <button
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={currentPage === totalPages - 1 || !hasDataOnLastPage}
          data-testid="page-last"
          className="ml-0.5 text-sm hover:text-white text-primary font-semibold py-2 px-4 rounded-r-md"
          aria-label="Last page"
        >
          <SkipNextIcon sx={{ color: "#641752" }} />
        </button>
      </div>
    </nav>
  );
};
